import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { UsersApiHelperForMember } from "api/ApiHelper";
import { AppContext } from "App";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { v4 as uuidv4 } from "uuid";

import { calculateAmortizationSchedule } from "../forms/amortizationCalculator";
import { calculateScheduledPayment } from "../forms/restructuringHelper";

// Extend dayjs with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Custom hook to manage amortization schedule data and calculations
 * @param {string} debt_schedule_id - The debt schedule ID
 * @param {number} year - The selected year for calculations
 * @param {Object} globalState - Global state object containing producer_token
 * @returns {Object} Hook return object with schedule data and functions
 */
export default function useDebtScheduleAmortization(debt_schedule_id, year, globalState) {
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [debtSchedule, setDebtSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const api = UsersApiHelperForMember;

  /**
   * Load debt schedule to get interest_only_periods
   */
  const loadDebtSchedule = useCallback(async () => {
    if (!debt_schedule_id || String(debt_schedule_id) === "0") {
      setDebtSchedule(null);
      return;
    }

    try {
      const response = await api.callPost(
        "debtSchedule/Search",
        {
          id: debt_schedule_id,
          producer_token: globalState?.producer_token,
          is_active: true,
        },
        globalState
      );
      if (response && response.length > 0) {
        setDebtSchedule(response[0]);
      }
    } catch (error) {
      console.error("Error loading debt schedule", error);
    }
  }, [debt_schedule_id, globalState?.producer_token]);

  /**
   * Load amortization schedule from API
   */
  const loadSchedule = useCallback(async () => {
    // Don't load if debt_schedule_id is not valid
    if (!debt_schedule_id || String(debt_schedule_id) === "0") {
      setAmortizationSchedule([]);
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        producer_token: globalState.producer_token,
        debt_schedule_id: debt_schedule_id,
        is_active: true,
      };

      const response = await api.callPost("DebtScheduleAmortization/search", payload, globalState);
      if (response && Array.isArray(response)) {
        // Sort by payment_number ascending
        const sortedResponse = [...response].sort((a, b) => a.payment_number - b.payment_number);
        setAmortizationSchedule(sortedResponse);
      } else {
        setAmortizationSchedule([]);
      }
    } catch (error) {
      console.error("Error loading amortization schedule", error);
      throw new Error(`Failed to load amortization schedule: ${error.message || error.detail || "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  }, [debt_schedule_id, globalState?.producer_token]);

  /**
   * Load debt schedule and amortization schedule on mount and when dependencies change
   */
  useEffect(() => {
    if (globalState?.producer_token) {
      loadDebtSchedule();
      loadSchedule();
    }
  }, [loadDebtSchedule, loadSchedule, globalState?.producer_token]);

  /**
   * Calculate derived fields from amortization schedule
   */
  const calculatedFields = useMemo(() => {
    if (!amortizationSchedule || amortizationSchedule.length === 0 || !year) {
      return null;
    }

    // Get year boundaries
    const yearStart = dayjs(`${year}-01-01`);
    const yearEnd = dayjs(`${year}-12-31`);

    // Filter payments within the selected year
    const paymentsInYear = amortizationSchedule.filter((payment) => {
      const paymentDate = dayjs(payment.payment_date);
      return paymentDate.isSameOrAfter(yearStart) && paymentDate.isSameOrBefore(yearEnd);
    });

    // Get last payment on or before year-end
    const paymentsUpToYearEnd = amortizationSchedule.filter((payment) => {
      const paymentDate = dayjs(payment.payment_date);
      return paymentDate.isSameOrBefore(yearEnd);
    });
    const lastPaymentInYear = paymentsUpToYearEnd[paymentsUpToYearEnd.length - 1];

    // Get future payments (after year-end)
    const futurePayments = amortizationSchedule.filter((payment) => {
      const paymentDate = dayjs(payment.payment_date);
      return paymentDate.isAfter(yearEnd);
    });

    // Calculate fields - get the regular scheduled payment amount
    // Get the first payment AFTER the interest-only periods
    const interestOnlyPeriods = parseInt(debtSchedule?.interest_only_periods || 0, 10);
    const payment_amount = calculateScheduledPayment(amortizationSchedule, interestOnlyPeriods) || 0;

    const interest_paid = paymentsInYear.reduce((sum, payment) => {
      return sum + parseFloat(payment.interest_payment || 0);
    }, 0);

    const principal_paid = paymentsInYear.reduce((sum, payment) => {
      return sum + parseFloat(payment.principal_payment || 0) + parseFloat(payment.extra_principal_payment || 0);
    }, 0);

    const principal_total = lastPaymentInYear ? parseFloat(lastPaymentInYear.ending_balance || 0) : 0;

    // Calculate liability buckets based on payment dates from year-end
    const oneYearOut = yearEnd.add(1, "year");
    const tenYearsOut = yearEnd.add(10, "year");

    const liability_current = futurePayments
      .filter((payment) => {
        const paymentDate = dayjs(payment.payment_date);
        return paymentDate.isSameOrBefore(oneYearOut);
      })
      .reduce((sum, payment) => {
        return sum + parseFloat(payment.principal_payment || 0);
      }, 0);

    const liability_intermediate = futurePayments
      .filter((payment) => {
        const paymentDate = dayjs(payment.payment_date);
        return paymentDate.isAfter(oneYearOut) && paymentDate.isSameOrBefore(tenYearsOut);
      })
      .reduce((sum, payment) => {
        return sum + parseFloat(payment.principal_payment || 0);
      }, 0);

    const liability_longterm = futurePayments
      .filter((payment) => {
        const paymentDate = dayjs(payment.payment_date);
        return paymentDate.isAfter(tenYearsOut);
      })
      .reduce((sum, payment) => {
        return sum + parseFloat(payment.principal_payment || 0);
      }, 0);

    // Calculate accrued interest from last payment to year-end
    let interest_accrued = 0;
    if (lastPaymentInYear && principal_total > 0) {
      const lastPaymentDate = dayjs(lastPaymentInYear.payment_date);
      const daysAccrued = yearEnd.diff(lastPaymentDate, "day");

      // We need the interest rate from somewhere - we'll calculate it based on the payment data
      // Or we could require it to be passed in. For now, we'll estimate it from the schedule
      const interestRate =
        lastPaymentInYear.interest_payment > 0 && lastPaymentInYear.beginning_balance > 0
          ? parseFloat(lastPaymentInYear.interest_payment) / parseFloat(lastPaymentInYear.beginning_balance)
          : 0;

      // Annual rate approximation - this is a simplification
      const estimatedAnnualRate = interestRate * 12; // Assuming monthly payments for simplification

      if (daysAccrued > 0) {
        interest_accrued = principal_total * (estimatedAnnualRate / 365) * daysAccrued;
      }
    }

    return {
      payment_amount: payment_amount.toFixed(2),
      interest_paid: interest_paid.toFixed(2),
      principal_paid: principal_paid.toFixed(2),
      principal_total: principal_total.toFixed(2),
      liability_current: liability_current.toFixed(2),
      liability_intermediate: liability_intermediate.toFixed(2),
      liability_longterm: liability_longterm.toFixed(2),
      interest_accrued: interest_accrued.toFixed(2),
    };
  }, [amortizationSchedule, debtSchedule, year]);

  /**
   * Check if amortization schedule exists
   */
  const hasExistingData = useCallback(() => {
    return amortizationSchedule && amortizationSchedule.length > 0;
  }, [amortizationSchedule]);

  /**
   * Generate and save amortization schedule
   * @param {Object} debtScheduleState - The debt schedule state object
   */
  const generateAmortizationSchedule = useCallback(
    async (debtScheduleState) => {
      try {
        setIsLoading(true);

        // Validate required fields
        const errors = [];
        if (!debtScheduleState.amount || parseFloat(debtScheduleState.amount) <= 0) {
          errors.push("Original Amount");
        }
        if (debtScheduleState.interest_rate === undefined || debtScheduleState.interest_rate === null) {
          errors.push("Interest Rate");
        }
        if (!debtScheduleState.inception_date) {
          errors.push("Inception Date");
        }
        if (!debtScheduleState.maturity_date) {
          errors.push("Maturity Date");
        }
        if (!debtScheduleState.payment_frequency) {
          errors.push("Payment Frequency");
        }

        if (errors.length > 0) {
          throw new Error(`Please complete all required fields: ${errors.join(", ")}`);
        }

        // Calculate the schedule using the utility function
        const schedule = calculateAmortizationSchedule({
          inception_date: debtScheduleState.inception_date,
          maturity_date: debtScheduleState.maturity_date,
          interest_rate: parseFloat(debtScheduleState.interest_rate), // Already stored as decimal by MASK_PERCENTAGE
          payment_frequency: debtScheduleState.payment_frequency,
          amount: parseFloat(debtScheduleState.amount),
          balloon_amount: parseFloat(debtScheduleState.balloon_amount || 0),
          extra_principal_rate: parseFloat(debtScheduleState.extra_principal_rate || 0), // Already stored as decimal by MASK_PERCENTAGE
          day_count_convention: debtScheduleState.day_count_convention || "30/360",
          interest_only_periods: parseInt(debtScheduleState.interest_only_periods || 0, 10),
        });

        // First, deactivate any existing records if they exist
        if (hasExistingData()) {
          const deactivatePayload = amortizationSchedule.map((record) => ({
            ...record,
            is_active: false,
          }));

          await api.callPost(
            "DebtScheduleAmortization",
            {
              DebtScheduleAmortization: deactivatePayload,
              producer_token: globalState.producer_token,
            },
            globalState
          );
        }

        // Prepare records for saving with proper schema
        const recordsToSave = schedule.map((payment) => ({
          producer_token: globalState.producer_token,
          debt_schedule_id: debt_schedule_id,
          id: uuidv4(),
          payment_number: payment.payment_number,
          payment_date: payment.payment_date,
          beginning_balance: payment.beginning_balance,
          payment_amount: payment.payment_amount,
          principal_payment: payment.principal_payment,
          interest_payment: payment.interest_payment,
          ending_balance: payment.ending_balance,
          cumulative_principal: payment.cumulative_principal,
          cumulative_interest: payment.cumulative_interest,
          extra_principal_payment: payment.extra_principal_payment,
          extra_interest_payment: payment.extra_interest_payment,
          notes: payment.notes,
          is_actual: payment.is_actual,
          actual_payment_date: payment.actual_payment_date,
          actual_payment_amount: payment.actual_payment_amount,
          is_active: true,
        }));

        await api.callPost(
          "DebtScheduleAmortization",
          {
            DebtScheduleAmortization: recordsToSave,
            producer_token: globalState.producer_token,
          },
          globalState
        );

        // Update local state directly with calculated schedule
        setAmortizationSchedule(recordsToSave);

        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Error generating amortization schedule", error);
        throw error;
      }
    },
    [debt_schedule_id, globalState, hasExistingData, amortizationSchedule, loadSchedule, api]
  );

  /**
   * Reload schedule (public wrapper for loadSchedule)
   */
  const reloadSchedule = useCallback(() => {
    return loadSchedule();
  }, [loadSchedule]);

  return {
    amortizationSchedule,
    calculatedFields,
    generateAmortizationSchedule,
    hasExistingData,
    reloadSchedule,
    isLoading,
  };
}
